/**
 * Border chevrons (MapDef.borderChevrons): one chevron centred on every border
 * of the listed territories where attacking in rolls custom dice, pointing in.
 * Colour follows the dice, as route colour does: red where the attacker is
 * better off (Royal Rumble: storming the Sand is d8; Hexcore's Core defends with
 * d4), orange (`kind: 'disadvantage'`) where worse (Normulli Sector: plains
 * attack towers at 5 v 7). Once a territory has any custom dice, its borders
 * fought at the standard 6 v 6 get none.
 *
 * Placement is shared by MapView, the map editor (baked by
 * tools/map-editor/extract-maps.ts) and the cold-start bundle, so the game and
 * the editor show the same chevrons.
 */
import { parsePolygonVertices } from './badgeHelpers'

export interface BorderChevron { x: number; y: number; angle: number; kind?: 'disadvantage' }

type ChevronDice = number | { attack?: number; defend?: number }
interface ChevronTerritory { id: string; shape?: string | undefined; borderDice?: Record<string, ChevronDice> | undefined }
interface ChevronMap {
  territories: ReadonlyArray<ChevronTerritory>
  borderChevrons?: { territoryIds: ReadonlyArray<string> } | undefined
  viewBox: { width: number }
}

const insidePolygon = ([x, y]: [number, number], poly: Array<[number, number]>): boolean => {
  let hit = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!, [xj, yj] = poly[j]!
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit
  }
  return hit
}

/**
 * One chevron per border of each listed territory: anchored at the point halfway
 * along the border line it shares with a neighbour, pointing in. The anchor is
 * ON the border; borderChevronPoints draws the chevron inside from there.
 * Borders are the polygon edges the two territories share (the generator gives
 * neighbours identical shared vertices).
 */
export function computeBorderChevrons(map: ChevronMap): BorderChevron[] {
  const ids = map.borderChevrons?.territoryIds ?? []
  if (ids.length === 0) return []
  const key = ([x, y]: [number, number]) => `${Math.round(x * 10)},${Math.round(y * 10)}`
  const edgeKey = (a: [number, number], b: [number, number]) => [key(a), key(b)].sort().join('|')
  const owners = new Map<string, Set<string>>()   // edge -> territories that have it
  const polys = new Map<string, Array<[number, number]>>()
  for (const t of map.territories) {
    const poly = parsePolygonVertices(t.shape)
    polys.set(t.id, poly)
    for (let i = 0; i < poly.length; i++) {
      const k = edgeKey(poly[i]!, poly[(i + 1) % poly.length]!)
      if (!owners.has(k)) owners.set(k, new Set())
      owners.get(k)!.add(t.id)
    }
  }
  const diceOf = new Map(map.territories.map((t) => [t.id, t.borderDice] as const))
  const sides = (spec: ChevronDice | undefined, mode: 'attack' | 'defend'): number => {
    const n = typeof spec === 'number' ? spec : spec?.[mode]
    return typeof n === 'number' && n >= 2 ? n : 6
  }
  const chevrons: BorderChevron[] = []
  for (const id of ids) {
    const poly = polys.get(id)
    if (!poly || poly.length < 3) continue
    // A territory whose borders carry no custom dice at all keeps a chevron on
    // every border; otherwise only the borders that fight at custom dice get one.
    const diceAware = !!diceOf.get(id) || map.territories.some((t) => t.borderDice?.[id] !== undefined)
    // Group this territory's edges by the neighbour that shares them, in ring order.
    const byNeighbour = new Map<string, Array<[[number, number], [number, number]]>>()
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i]!, b = poly[(i + 1) % poly.length]!
      for (const other of owners.get(edgeKey(a, b)) ?? []) {
        if (other === id) continue
        if (!byNeighbour.has(other)) byNeighbour.set(other, [])
        byNeighbour.get(other)!.push([a, b])
      }
    }
    for (const [neighbour, segs] of byNeighbour) {
      // What attacking in across this border rolls: the neighbour's attack dice
      // against this territory's defence dice.
      const attack = sides(diceOf.get(neighbour)?.[id], 'attack')
      const defend = sides(diceOf.get(id)?.[neighbour], 'defend')
      if (diceAware && attack === 6 && defend === 6) continue
      const worse = attack < 6 || defend > 6
      const total = segs.reduce((s, [a, b]) => s + Math.hypot(b[0] - a[0], b[1] - a[1]), 0)
      let walked = 0
      for (const [a, b] of segs) {
        const len = Math.hypot(b[0] - a[0], b[1] - a[1])
        if (walked + len < total / 2) { walked += len; continue }
        const t = len === 0 ? 0 : (total / 2 - walked) / len
        const ux = (b[0] - a[0]) / (len || 1), uy = (b[1] - a[1]) / (len || 1)
        let nx = -uy, ny = ux
        const px = a[0] + (b[0] - a[0]) * t, py = a[1] + (b[1] - a[1]) * t
        if (!insidePolygon([px + nx * 2, py + ny * 2], poly)) { nx = -nx; ny = -ny }
        chevrons.push({
          x: Math.round(px * 100) / 100,
          y: Math.round(py * 100) / 100,
          angle: Math.round(((Math.atan2(ny, nx) * 180) / Math.PI) * 10) / 10,
          ...(worse ? { kind: 'disadvantage' as const } : {}),
        })
        break
      }
    }
  }
  return chevrons
}

/** Outline (halo) stroke width of a chevron of `size`; its round ends reach half of it. */
export const borderChevronHaloWidth = (size: number): number => size * 0.5

/**
 * The chevron as SVG polyline points: a ">" of `size`, tip pointing along
 * `angle`, drawn entirely inside the territory it marks. Its open back sits one
 * outline radius in from the anchor (the border), so the outlined back corners
 * just touch the territory's edge and never spill into the neighbour. The map
 * editor mirrors this in tools/map-editor/app.js (borderChevronPoints); keep
 * the two in step.
 */
export function borderChevronPoints(c: BorderChevron, size: number): string {
  const a = (c.angle * Math.PI) / 180, ux = Math.cos(a), uy = Math.sin(a), vx = -uy, vy = ux
  const backAt = borderChevronHaloWidth(size) / 2
  const tip: [number, number] = [c.x + ux * (backAt + size), c.y + uy * (backAt + size)]
  const back = (s: number): [number, number] => [c.x + ux * backAt + vx * size * 0.6 * s, c.y + uy * backAt + vy * size * 0.6 * s]
  return [back(1), tip, back(-1)].map(([x, y]) => `${Math.round(x * 100) / 100},${Math.round(y * 100) / 100}`).join(' ')
}
